import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/** @param {HTMLElement} widget */
function getQuantityInput(widget) {
  const form = widget.closest('product-form-component');
  if (!form) return null;
  return /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
}

/** @param {HTMLElement} widget */
function getQuantitySelector(widget) {
  const form = widget.closest('product-form-component');
  if (!form) return null;
  return form.querySelector('quantity-selector-component');
}

/** @param {HTMLElement} widget */
function getCurrentQuantity(widget) {
  const input = getQuantityInput(widget);
  if (!input) return 1;
  return Number.parseInt(input.value || '1', 10) || 1;
}

/** @param {HTMLElement} widget */
function updateActiveOption(widget) {
  const qty = getCurrentQuantity(widget);
  widget.querySelectorAll('[data-bundle-qty]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    const buttonQty = Number.parseInt(button.dataset.bundleQty || '0', 10);
    button.classList.toggle('is-active', buttonQty === qty);
    button.setAttribute('aria-pressed', String(buttonQty === qty));
  });
}

/** @param {HTMLElement} widget */
function renderProgress(widget) {
  const targetQty = Number.parseInt(widget.dataset.targetQty || '0', 10);
  const promoTotal = widget.dataset.promoTotal || '';
  const qty = getCurrentQuantity(widget);
  const progress = widget.querySelector('[data-bundle-progress]');
  if (!(progress instanceof HTMLElement) || targetQty <= 0) return;

  if (qty >= targetQty) {
    const active = `Offerta: ${targetQty} confezioni a ${promoTotal}. Lo sconto si applica automaticamente nel carrello.`;
    progress.textContent = active;
  } else {
    const missing = targetQty - qty;
    const missingText = missing === 1 ? 'confezione' : 'confezioni';
    const pending = `Aggiungi ancora ${missing} ${missingText} per attivare l'offerta a ${promoTotal}.`;
    progress.textContent = pending;
  }
}

/** @param {HTMLElement} widget */
function renderSaving(widget) {
  const targetQty = Number.parseInt(widget.dataset.targetQty || '0', 10);
  const targetTotalCents = Number.parseInt(widget.dataset.targetTotalCents || '0', 10);
  const unitPriceCents = Number.parseInt(widget.dataset.unitPriceCents || '0', 10);
  const moneyFormat = widget.dataset.moneyFormat || '{{amount}}';
  const currency = widget.dataset.currency || 'EUR';
  const optionSavingNode = widget.querySelector('[data-bundle-option-saving]');
  if (!(optionSavingNode instanceof HTMLElement) || targetQty <= 0 || targetTotalCents <= 0 || unitPriceCents <= 0) return;

  const regularTotal = unitPriceCents * targetQty;
  const savingCents = regularTotal - targetTotalCents;

  if (savingCents <= 0) {
    if (optionSavingNode instanceof HTMLElement) {
      optionSavingNode.textContent = '';
    }
    return;
  }

  let savingText = '';
  try {
    savingText = formatMoney(savingCents, moneyFormat, currency);
  } catch {
    savingText = `€${(savingCents / 100).toFixed(2).replace('.', ',')}`;
  }

  if (optionSavingNode instanceof HTMLElement) {
    optionSavingNode.textContent = `Risparmi ${savingText}`;
  }
}

/** @param {HTMLElement} widget */
function updateOptionPrices(widget) {
  const targetQty = Number.parseInt(widget.dataset.targetQty || '0', 10);
  const targetTotalCents = Number.parseInt(widget.dataset.targetTotalCents || '0', 10);
  const unitPriceCents = Number.parseInt(widget.dataset.unitPriceCents || '0', 10);
  const moneyFormat = widget.dataset.moneyFormat || '{{amount}}';
  const currency = widget.dataset.currency || 'EUR';

  widget.querySelectorAll('[data-bundle-price]').forEach((priceNode) => {
    if (!(priceNode instanceof HTMLElement)) return;
    const qty = Number.parseInt(priceNode.dataset.qty || '1', 10);
    let cents = unitPriceCents * qty;
    if (qty === targetQty && targetTotalCents > 0) {
      cents = targetTotalCents;
    }

    try {
      priceNode.textContent = formatMoney(cents, moneyFormat, currency);
    } catch {
      const fallback = (cents / 100).toFixed(2).replace('.', ',');
      priceNode.textContent = `€${fallback}`;
    }
  });
}

/** @param {HTMLElement} widget */
function refreshWidget(widget) {
  updateOptionPrices(widget);
  updateActiveOption(widget);
  renderProgress(widget);
  renderSaving(widget);
}

function refreshAllWidgets() {
  document.querySelectorAll('[data-bundle-promo]').forEach((widget) => {
    if (!(widget instanceof HTMLElement)) return;
    refreshWidget(widget);
  });
}

function setBundleQuantity(widget, quantity) {
  const input = getQuantityInput(widget);
  if (!input) return;

  const selector = getQuantitySelector(widget);
  if (selector && 'setValue' in selector) {
    selector.setValue(String(quantity));
    if ('onQuantityChange' in selector && typeof selector.onQuantityChange === 'function') {
      selector.onQuantityChange();
    }
    if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
      selector.updateButtonStates();
    }
  } else {
    input.value = String(quantity);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  refreshWidget(widget);
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  const button = target.closest('[data-bundle-qty]');
  if (!(button instanceof HTMLButtonElement)) return;

  const widget = button.closest('[data-bundle-promo]');
  if (!(widget instanceof HTMLElement)) return;

  const qty = Number.parseInt(button.dataset.bundleQty || '0', 10);
  if (qty <= 0) return;

  event.preventDefault();
  setBundleQuantity(widget, qty);
});

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  const widget = event.target.closest('product-form-component')?.querySelector('[data-bundle-promo]');
  if (!(widget instanceof HTMLElement)) return;
  refreshWidget(widget);
});

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const variant = event.detail?.resource;
  if (!variant || typeof variant.price !== 'number') return;

  document.querySelectorAll('[data-bundle-promo]').forEach((widget) => {
    if (!(widget instanceof HTMLElement)) return;
    const productId = widget.dataset.productId;
    if (productId && event.detail?.data?.productId && productId !== String(event.detail.data.productId)) return;

    widget.dataset.unitPriceCents = String(variant.price);
    refreshWidget(widget);
  });
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', refreshAllWidgets);
} else {
  refreshAllWidgets();
}
